import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  UndoOutlined, RedoOutlined, VisibilityOutlined, VisibilityOffOutlined,
  AddOutlined, SaveOutlined, CheckCircleOutlined, ErrorOutlined,
  MoreVertOutlined, DriveFileRenameOutlineOutlined,
  ContentCopyOutlined, DeleteOutlineOutlined, OpenInNewOutlined, SettingsOutlined, PublicOutlined,
  HomeOutlined, AutoAwesomeOutlined, NoteAddOutlined,
} from '@mui/icons-material';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, Popover, Dialog, DialogContent } from '@mui/material';

import { PRIMARY, BREAKPOINTS, ELEMENT_TYPES } from './constants';
import { CtxMenuCtx, PreviewCtx, DragCtx, PageCtx } from './contexts';
import {
  uid,
  removeEl, insertIntoContainer, insertIntoSection, updateEl,
  reorderInContainer, isDescendantOrSelf,
} from './utils';

import { SectionSettings, ContainerSettings, TextInspector, ButtonInspector, ImageInspector, LeftPanelIdle, PageSettingsContent } from './inspectors';
import { CountdownInspector } from './inspectors/CountdownInspector';
import { SpeakersInspector } from './inspectors/SpeakersInspector';
import { ScheduleInspector } from './inspectors/ScheduleInspector';
import { HeaderInspector } from './inspectors/HeaderInspector';
import { FooterInspector } from './inspectors/FooterInspector';
import { Section, ContextMenu } from './canvas/Section';
import { HeaderSection } from './canvas/HeaderSection';
import { FooterSection } from './canvas/FooterSection';
import { makeHeaderSection, makeFooterSection } from './utils';
import { LayersPanel } from './panels/LayersPanel';
import { api } from '../../../hooks/useApi';
import { PAGE_TEMPLATES } from './inspectors/templates';
import { useTemplateThumbnails } from './inspectors/templates/useTemplateThumbnails';

// ── Default global layout configs ─────────────────────────────────────────────
const DEFAULT_GLOBAL_HEADER = {
  template: 'classic',
  logoSrc: '', logoText: 'Event', logoHeight: 36, logoAlt: '',
  links: [],
  showLogin: true, loginLabel: 'Login',
  showRegister: true, registerLabel: 'Register Now',
  style: {
    background: '#ffffff', linkColor: '#1a1a2e',
    loginColor: '#1075BA', registerBg: '#1075BA', registerColor: '#ffffff',
    pillBg: '#ffffff', height: 72,
  },
};

const DEFAULT_GLOBAL_FOOTER = {
  template: 'columns',
  logoSrc: '', logoText: 'Event', logoHeight: 32, logoAlt: '',
  tagline: '', links: [],
  copyrightText: `© ${new Date().getFullYear()} My Company. All rights reserved.`,
  style: {
    background: '#ffffff', logoColor: '#1a1a2e',
    linkColor: '#5a6474', linkHoverColor: '#1075BA',
    copyrightColor: '#9ea8b3', borderColor: '#e8edf2',
    paddingX: '48px', paddingY: '40px',
  },
};

// ── Page helpers ──────────────────────────────────────────────────────────────
function slugify(str) {
  return str.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'page';
}

function makeInitialPage(title, order, isDefault) {
  title     = title     !== undefined ? title     : 'Page 1';
  order     = order     !== undefined ? order     : 0;
  isDefault = isDefault !== undefined ? isDefault : false;
  // Start with header + footer only — no content sections.
  // The wizard will show so the user can pick a template or blank.
  const sections = [makeHeaderSection(), makeFooterSection()];
  return {
    _id: uid(), _isNew: true,
    title, slug: slugify(title), order,
    published: false, isDefault,
    sections,
    headerOverride: false, footerOverride: false,
    history: [sections], histIdx: 0,
  };
}

function pageFromApi(doc) {
  let sections = doc.sections || [];
  if (!sections.length || sections[0]?.type !== 'header')
    sections = [makeHeaderSection(), ...sections];
  if (sections[sections.length - 1]?.type !== 'footer')
    sections = [...sections, makeFooterSection()];
  return {
    _id: String(doc._id), _isNew: false,
    title: doc.title || 'Untitled',
    slug: doc.slug || slugify(doc.title || 'page'),
    order: doc.order || 0,
    published: doc.published || false,
    isDefault: doc.isDefault || false,
    sections,
    headerOverride: doc.headerOverride || false,
    footerOverride: doc.footerOverride || false,
    history: [sections], histIdx: 0,
  };
}

// ── PageWizard ────────────────────────────────────────────────────────────────
// Shown when a page has no content sections yet.
// Shows first 2 templates + a blank option as tall vertical cards.
function PageWizard({ onApplyTemplate, onBlank, thumbnails }) {
  const WIZARD_TEMPLATES = PAGE_TEMPLATES.slice(0, 2);

  const ACCENT_COLORS = ['#1075BA', '#16a34a'];

  return (
    <Box sx={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', p: 4, gap: 3,
      background: 'linear-gradient(160deg, #f8fafc 0%, #f0f4f8 100%)',
      minHeight: '100%',
    }}>
      {/* Heading */}
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '12px', background: alpha(PRIMARY, 0.1), mb: 1.5 }}>
          <AutoAwesomeOutlined sx={{ fontSize: 22, color: PRIMARY }} />
        </Box>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>
          Create a new page
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: '#9ea8b3', mt: 0.75, fontWeight: 400 }}>
          Start from a template or build from scratch
        </Typography>
      </Box>

      {/* Cards row */}
      <Box sx={{
        display: 'flex', flexDirection: 'row', gap: 2.5,
        width: '100%', maxWidth: 780,
        alignItems: 'flex-start',
      }}>

        {/* Template cards */}
        {WIZARD_TEMPLATES.map((tpl, i) => {
          const accent = ACCENT_COLORS[i] || PRIMARY;
          const thumb  = thumbnails?.[tpl.id];
          return (
            <Box
              key={tpl.id}
              onClick={() => onApplyTemplate(tpl)}
              sx={{
                flex: 1,
                display: 'flex', flexDirection: 'column',
                borderRadius: 3,
                border: '2px solid #e4e9f0',
                overflow: 'hidden',
                cursor: 'pointer',
                background: '#fff',
                transition: 'all 0.18s',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                '&:hover': {
                  borderColor: accent,
                  boxShadow: `0 8px 32px ${alpha(accent, 0.18)}`,
                  transform: 'translateY(-3px)',
                },
              }}
            >
              {/* Thumbnail */}
              <Box sx={{
                background: '#f7f8f9',
                position: 'relative', overflow: 'hidden', flexShrink: 0,
                p: 1.5,
              }}>
                {thumb ? (
                  <Box
                    component="img"
                    src={thumb}
                    alt={tpl.name}
                    sx={{ width: '100%', height: 'auto', display: 'block', borderRadius: 1.5, boxShadow: '0 2px 12px rgba(0,0,0,0.10)' }}
                  />
                ) : (
                  /* Skeleton bars while generating */
                  <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: '6px', minHeight: 180, background: `linear-gradient(135deg, ${alpha(accent, 0.06)} 0%, ${alpha(accent, 0.12)} 100%)`, borderRadius: 1.5 }}>
                    <Box sx={{ height: 14, borderRadius: 1, background: alpha(accent, 0.5), width: '55%' }} />
                    <Box sx={{ height: 9,  borderRadius: 1, background: alpha(accent, 0.25), width: '85%' }} />
                    <Box sx={{ height: 9,  borderRadius: 1, background: alpha(accent, 0.18), width: '70%' }} />
                    <Box sx={{ display: 'flex', gap: '6px', mt: '6px' }}>
                      {[1,2,3].map(k => <Box key={k} sx={{ flex: 1, height: 60, borderRadius: 1.5, background: alpha(accent, 0.12) }} />)}
                    </Box>
                    <Box sx={{ height: 22, borderRadius: 10, background: alpha(accent, 0.35), width: '45%', alignSelf: 'center', mt: '8px' }} />
                    <Typography sx={{ fontSize: '0.62rem', color: accent, opacity: 0.6, textAlign: 'center', mt: 'auto', pb: 1 }}>
                      Generating preview…
                    </Typography>
                  </Box>
                )}
                {/* Badge */}
                <Box sx={{
                  position: 'absolute', top: 10, right: 10,
                  background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)',
                  color: '#fff', borderRadius: 1, px: 1, py: 0.3,
                  fontSize: '0.6rem', fontWeight: 700,
                }}>
                  {tpl.sections.length} sections
                </Box>
              </Box>

              {/* Info */}
              <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AutoAwesomeOutlined sx={{ fontSize: 13, color: accent }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e' }}>{tpl.name}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', lineHeight: 1.5 }}>{tpl.description}</Typography>
              </Box>

              {/* Apply button */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Box sx={{
                  py: 1, borderRadius: 2, background: accent, color: '#fff',
                  fontSize: '0.75rem', fontWeight: 700, textAlign: 'center',
                  letterSpacing: '0.03em',
                }}>
                  Use this template
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* Blank page card */}
        <Box
          onClick={onBlank}
          sx={{
            flex: 1,
            display: 'flex', flexDirection: 'column',
            borderRadius: 3,
            border: '2px dashed #d1d9e0',
            overflow: 'hidden',
            cursor: 'pointer',
            background: '#fff',
            transition: 'all 0.18s',
            boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
            '&:hover': {
              borderColor: PRIMARY,
              boxShadow: `0 8px 32px ${alpha(PRIMARY, 0.1)}`,
              transform: 'translateY(-3px)',
            },
          }}
        >
          {/* Blank visual */}
          <Box sx={{
            flexShrink: 0,
            background: '#f8fafc',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 1.5,
            py: 2.75,
          }}>
            <Box sx={{
              width: 56, height: 56, borderRadius: '16px',
              background: alpha(PRIMARY, 0.08),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <NoteAddOutlined sx={{ fontSize: 28, color: alpha(PRIMARY, 0.5) }} />
            </Box>
            {/* Mini blank page illustration */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px', width: '60%', mt: 1 }}>
              {[70, 90, 55, 80, 40].map((w, i) => (
                <Box key={i} sx={{ height: 5, borderRadius: 2, background: '#e4e9f0', width: `${w}%` }} />
              ))}
            </Box>
          </Box>

          {/* Info */}
          <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <NoteAddOutlined sx={{ fontSize: 13, color: '#9ea8b3' }} />
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e' }}>Blank Page</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', lineHeight: 1.5 }}>Start with a clean canvas and build everything from scratch.</Typography>
          </Box>

          {/* Start button */}
          <Box sx={{ px: 2, pb: 2 }}>
            <Box sx={{
              py: 1, borderRadius: 2,
              border: `1.5px solid #e4e9f0`,
              color: '#5a6474',
              fontSize: '0.75rem', fontWeight: 700, textAlign: 'center',
              letterSpacing: '0.03em',
            }}>
              Start blank
            </Box>
          </Box>
        </Box>

      </Box>
    </Box>
  );
}

// ── PageTabs ──────────────────────────────────────────────────────────────────
function PageTabs({ pages, activePageId, onSwitch, onAdd, onRename, onDuplicate, onDelete }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal]   = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuPageId, setMenuPageId] = useState(null);
  const inputRef = useRef(null);

  React.useEffect(() => { if (renamingId && inputRef.current) inputRef.current.select(); }, [renamingId]);

  const startRename  = (page) => { setMenuAnchor(null); setRenamingId(page._id); setRenameVal(page.title); };
  const commitRename = () => { if (renamingId && renameVal.trim()) onRename(renamingId, renameVal.trim()); setRenamingId(null); };

  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-end',
      px: 2, pt: '8px', borderBottom: '1px solid #e8edf2',
      background: '#f5f7fa', overflowX: 'auto', flexShrink: 0,
      '&::-webkit-scrollbar': { height: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 2 },
    }}>
      {pages.map((page) => {
        const isActive = page._id === activePageId;
        return (
          <Box
            key={page._id}
            onClick={() => { if (!renamingId) onSwitch(page._id); }}
            onDoubleClick={() => startRename(page)}
            sx={{
              position: 'relative', flexShrink: 0,
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1.5, height: 34, cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              border: isActive ? '1px solid #e8edf2' : '1px solid transparent',
              borderBottom: isActive ? '1px solid #fff' : '1px solid transparent',
              background: isActive ? '#fff' : 'transparent',
              mb: '-1px', transition: 'all 0.12s',
              '&:hover': { background: isActive ? '#fff' : 'rgba(0,0,0,0.04)' },
              '&:hover .tab-ctx': { opacity: 1 },
            }}
          >
            {renamingId === page._id ? (
              <Box component="input" ref={inputRef} value={renameVal}
                onChange={e => setRenameVal(e.target.value)}
                onBlur={commitRename}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingId(null); }}
                onClick={e => e.stopPropagation()}
                sx={{ border: 'none', outline: 'none', background: 'transparent', fontSize: '0.78rem', fontWeight: 600, color: '#1075BA', fontFamily: 'inherit', p: 0, width: Math.max(60, renameVal.length * 8) + 'px' }}
              />
            ) : (
              <>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: isActive ? 600 : 400, color: isActive ? '#1075BA' : '#7a8899', userSelect: 'none', whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {page.title}
                </Typography>
                {page.isDefault && (
                  <Tooltip title="Default page" placement="top">
                    <HomeOutlined sx={{ fontSize: 11, color: isActive ? '#1075BA' : '#f59e0b', flexShrink: 0, opacity: isActive ? 0.7 : 1 }} />
                  </Tooltip>
                )}
              </>
            )}
            <Box className="tab-ctx"
              onClick={e => { e.stopPropagation(); setMenuAnchor(e.currentTarget); setMenuPageId(page._id); }}
              sx={{ opacity: isActive ? 0.5 : 0, transition: 'opacity 0.12s', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '3px', flexShrink: 0, '&:hover': { background: 'rgba(0,0,0,0.08)', opacity: '1 !important' } }}
            >
              <MoreVertOutlined sx={{ fontSize: 12, color: '#5a6474' }} />
            </Box>
          </Box>
        );
      })}

      <Box onClick={onAdd} sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, mb: '2px', ml: 0.5, borderRadius: '6px', cursor: 'pointer', color: '#9ea8b3', transition: 'all 0.12s', '&:hover': { background: 'rgba(16,117,186,0.08)', color: '#1075BA' } }}>
        <AddOutlined sx={{ fontSize: 16 }} />
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} PaperProps={{ sx: { mt: 0.5, boxShadow: '0 4px 20px rgba(0,0,0,0.12)', borderRadius: 2, minWidth: 160 } }}>
        <MenuItem onClick={() => { startRename(pages.find(p => p._id === menuPageId)); }} sx={{ fontSize: '0.82rem' }}>
          <ListItemIcon><DriveFileRenameOutlineOutlined sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { onDuplicate(menuPageId); setMenuAnchor(null); }} sx={{ fontSize: '0.82rem' }}>
          <ListItemIcon><ContentCopyOutlined sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        {pages.length > 1 && [
          <Divider key="div" />,
          <MenuItem key="del" onClick={() => { onDelete(menuPageId); setMenuAnchor(null); }} sx={{ fontSize: '0.82rem', color: '#dc2626' }}>
            <ListItemIcon><DeleteOutlineOutlined sx={{ fontSize: 16, color: '#dc2626' }} /></ListItemIcon>
            <ListItemText>Delete page</ListItemText>
          </MenuItem>,
        ]}
      </Menu>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function HomePageTab({ event }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Global layout ──────────────────────────────────────────────────────────
  const [globalLayout, setGlobalLayout] = useState({
    header: DEFAULT_GLOBAL_HEADER,
    footer: DEFAULT_GLOBAL_FOOTER,
  });
  const updateGlobalHeader = useCallback((config) =>
    setGlobalLayout(prev => ({ ...prev, header: config })), []);
  const updateGlobalFooter = useCallback((config) =>
    setGlobalLayout(prev => ({ ...prev, footer: config })), []);

  // ── Pages ──────────────────────────────────────────────────────────────────
  const [[pages, activePageId], setPagesAndActive] = useState(() => {
    const first = makeInitialPage('Page 1', 0, true);
    return [[first], first._id];
  });

  const setPages = useCallback((updater) => {
    setPagesAndActive(([prevPages, prevActive]) => {
      const next = typeof updater === 'function' ? updater(prevPages) : updater;
      return [next, prevActive];
    });
  }, []);

  const setActivePageId = useCallback((idOrUpdater) => {
    setPagesAndActive(([prevPages, prevActive]) => {
      const next = typeof idOrUpdater === 'function' ? idOrUpdater(prevActive) : idOrUpdater;
      return [prevPages, next];
    });
  }, []);

  const currentPage = pages.find(p => p._id === activePageId) || pages[0];
  const sections    = currentPage.sections;
  const history     = currentPage.history;
  const histIdx     = currentPage.histIdx;

  // ── Config resolution ──────────────────────────────────────────────────────
  const headerSection = sections.find(s => s.type === 'header');
  const footerSection = sections.find(s => s.type === 'footer');
  const contentSections = sections.filter(s => s.type !== 'header' && s.type !== 'footer');

  const activeHeaderConfig = currentPage.headerOverride
    ? (headerSection?.headerConfig || globalLayout.header)
    : globalLayout.header;

  const activeFooterConfig = currentPage.footerOverride
    ? (footerSection?.footerConfig || globalLayout.footer)
    : globalLayout.footer;

  // ── Override helpers ───────────────────────────────────────────────────────
  const enableHeaderOverride = useCallback(() => {
    setPages(prev => prev.map(p => {
      if (p._id !== activePageId) return p;
      const forked = p.sections.map(s =>
        s.type === 'header' ? { ...s, headerConfig: JSON.parse(JSON.stringify(globalLayout.header)) } : s
      );
      return { ...p, headerOverride: true, sections: forked, history: [...p.history.slice(0, p.histIdx + 1), forked], histIdx: p.histIdx + 1 };
    }));
  }, [activePageId, globalLayout.header, setPages]);

  const disableHeaderOverride = useCallback(() => {
    setPages(prev => prev.map(p => p._id !== activePageId ? p : { ...p, headerOverride: false }));
  }, [activePageId, setPages]);

  const enableFooterOverride = useCallback(() => {
    setPages(prev => prev.map(p => {
      if (p._id !== activePageId) return p;
      const forked = p.sections.map(s =>
        s.type === 'footer' ? { ...s, footerConfig: JSON.parse(JSON.stringify(globalLayout.footer)) } : s
      );
      return { ...p, footerOverride: true, sections: forked, history: [...p.history.slice(0, p.histIdx + 1), forked], histIdx: p.histIdx + 1 };
    }));
  }, [activePageId, globalLayout.footer, setPages]);

  const disableFooterOverride = useCallback(() => {
    setPages(prev => prev.map(p => p._id !== activePageId ? p : { ...p, footerOverride: false }));
  }, [activePageId, setPages]);

  // ── History ────────────────────────────────────────────────────────────────
  const setPageState = useCallback((pageId, updater) => {
    setPagesAndActive(([prevPages, prevActive]) => [
      prevPages.map(p => p._id === pageId ? { ...p, ...updater(p) } : p),
      prevActive,
    ]);
  }, []);

  const pushHistory = useCallback((newSections) => {
    setPageState(activePageId, p => {
      const next = p.history.slice(0, p.histIdx + 1).concat([newSections]);
      return { sections: newSections, history: next, histIdx: next.length - 1 };
    });
  }, [activePageId, setPageState]);

  const undo = () => { if (histIdx > 0) setPageState(activePageId, p => ({ sections: p.history[p.histIdx - 1], histIdx: p.histIdx - 1 })); };
  const redo = () => { if (histIdx < history.length - 1) setPageState(activePageId, p => ({ sections: p.history[p.histIdx + 1], histIdx: p.histIdx + 1 })); };

  // ── Inspector update handlers ──────────────────────────────────────────────
  // Header: write to page section if overriding, else write to global
  const handleUpdateHeaderSection = useCallback((updated) => {
    if (currentPage.headerOverride) {
      pushHistory(sections.map(s => s.id === updated.id ? updated : s));
    } else {
      updateGlobalHeader(updated.headerConfig || {});
    }
  }, [currentPage.headerOverride, sections, pushHistory, updateGlobalHeader]);

  // Footer: same pattern
  // Local speakers state — optimistic, synced from event prop on mount
  const [localSpeakers, setLocalSpeakers] = useState(event?.speakers || []);
  const [localSchedule, setLocalSchedule] = useState(event?.schedule || []);

  // Keep in sync if the parent event prop changes
  useEffect(() => {
    setLocalSpeakers(event?.speakers || []);
    setLocalSchedule(event?.schedule  || []);
  }, [event]);

  const handleSpeakersUpdate = useCallback((updatedSpeakers) => {
    setLocalSpeakers(updatedSpeakers);
    if (!event?._id) return;
    api.put(`/events/${event._id}`, { speakers: updatedSpeakers }).catch(() => {});
  }, [event?._id]);

  const handleScheduleUpdate = useCallback((updatedSchedule, updatedSpeakers) => {
    setLocalSchedule(updatedSchedule);
    if (updatedSpeakers) setLocalSpeakers(updatedSpeakers);
    if (!event?._id) return;
    const payload = {
      schedule: updatedSchedule,
      ...(updatedSpeakers ? { speakers: updatedSpeakers } : {}),
    };
    api.put(`/events/${event._id}`, payload).catch(() => {});
  }, [event?._id]);

  const handleLoginRedirectChange = useCallback((href) => {
    if (!event?._id) return;
    api.put(`/events/${event._id}`, { login: { loginRedirectHref: href ?? null } }).catch(() => {});
  }, [event?._id]);

    const handleUpdateFooterSection = useCallback((updated) => {
    if (currentPage.footerOverride) {
      pushHistory(sections.map(s => s.id === updated.id ? updated : s));
    } else {
      updateGlobalFooter(updated.footerConfig || {});
    }
  }, [currentPage.footerOverride, sections, pushHistory, updateGlobalFooter]);

  const handleUpdateSection = useCallback((updated) => {
    pushHistory(sections.map(s => s.id === updated.id ? updated : s));
  }, [sections, pushHistory]);

  const handleUpdateSelectedEl = useCallback((updated) => {
    pushHistory(updateEl(sections, updated.id, () => updated));
  }, [sections, pushHistory]);

  const handleRenameSection = (sectionId, label) => {
    pushHistory(sections.map(s => s.id === sectionId ? { ...s, label } : s));
  };

  const handleRenameEl = useCallback((elId, name) => {
    pushHistory(updateEl(sections, elId, el => ({ ...el, name })));
  }, [sections, pushHistory]);

  // ── Page management ────────────────────────────────────────────────────────
  const handleAddPage = useCallback(() => {
    setPagesAndActive(([prevPages, prevActive]) => {
      const newPage = makeInitialPage(`Page ${prevPages.length + 1}`, prevPages.length, false);
      return [[...prevPages, newPage], newPage._id];
    });
  }, []);

  const handleRenamePage = useCallback((pageId, title) => {
    setPages(prev => prev.map(p => p._id === pageId ? { ...p, title, slug: slugify(title) } : p));
  }, [setPages]);

  const handleDuplicatePage = useCallback((pageId) => {
    setPagesAndActive(([prevPages, prevActive]) => {
      const src = prevPages.find(p => p._id === pageId);
      if (!src) return [prevPages, prevActive];
      const cloned = JSON.parse(JSON.stringify(src));
      const reId = (node) => { node.id = uid(); if (node.children) node.children = node.children.map(c => reId({ ...c })); return node; };
      cloned._id = uid(); cloned._isNew = true;
      cloned.title = `${src.title} (copy)`;
      cloned.slug = slugify(cloned.title) + '-' + Date.now().toString(36);
      cloned.isDefault = false;
      cloned.sections = cloned.sections.map(s => ({ ...s, id: uid(), elements: s.elements.map(el => reId(el)) }));
      cloned.history = [cloned.sections]; cloned.histIdx = 0;
      const idx  = prevPages.findIndex(p => p._id === pageId);
      const next = [...prevPages.slice(0, idx + 1), cloned, ...prevPages.slice(idx + 1)];
      return [next, cloned._id];
    });
  }, []);

  const handleDeletePage = useCallback((pageId) => {
    setPagesAndActive(([prevPages, prevActive]) => {
      if (prevPages.length <= 1) return [prevPages, prevActive];
      const target = prevPages.find(p => p._id === pageId);
      const idx    = prevPages.findIndex(p => p._id === pageId);
      const next   = prevPages.filter(p => p._id !== pageId);
      const nextActive = prevActive === pageId ? next[Math.max(0, idx - 1)]._id : prevActive;
      if (target && !target._isNew && event._id)
        api.delete(`/events/${event._id}/pages/${pageId}`).catch(() => {});
      return [next, nextActive];
    });
  }, [event._id]);

  const [selectedId, setSelectedId]               = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);

  const handleSwitchPage = useCallback((pageId) => {
    setActivePageId(pageId);
    setSelectedId(null);
    setSelectedSectionId(null);
    setSearchParams({ id: pageId }, { replace: true });
  }, [setSearchParams]);

  const selectEl      = useCallback((id) => { setSelectedId(id);        if (id) setSelectedSectionId(null); }, []);
  const selectSection = useCallback((id) => { setSelectedSectionId(id); if (id) setSelectedId(null);        }, []);

  const [breakpoint, setBreakpoint]   = useState('desktop');
  const [previewMode, setPreviewMode] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [clipboard, setClipboard]     = useState(null);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [visitModalOpen, setVisitModalOpen] = useState(false);

  // ── Save / load ────────────────────────────────────────────────────────────
  const [saveStatus, setSaveStatus]         = useState('idle');
  const [slugUnavailable, setSlugUnavailable] = useState(false);
  const saveTimerRef                        = useRef(null);
  const [pageLoaded, setPageLoaded]         = useState(false);
  const [allForms,   setAllForms]           = useState([]);
  const [allLobbies, setAllLobbies]         = useState([]);

  React.useEffect(() => {
    if (!event._id) { setPageLoaded(true); return; }
    Promise.all([
      api.get(`/events/${event._id}/pages`),
      api.get(`/events/${event._id}`),
      api.get(`/forms?eventId=${event._id}`).catch(() => ({ data: { data: [] } })),
      api.get(`/lobbies?eventId=${event._id}`).catch(() => ({ data: [] })),
    ]).then(([pagesRes, eventRes, formsRes, lobbiesRes]) => {
      if (eventRes.data?.success && eventRes.data?.data?.layout) {
        const { header, footer } = eventRes.data.data.layout;
        setGlobalLayout({ header: header || DEFAULT_GLOBAL_HEADER, footer: footer || DEFAULT_GLOBAL_FOOTER });
      }
      const res = pagesRes.data;
      if (res.success && Array.isArray(res.data) && res.data.length) {
        const loaded = res.data.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(pageFromApi);
        const urlId = searchParams.get('id');
        const targetId = loaded.find(p => p._id === urlId) ? urlId : loaded[0]._id;
        setPagesAndActive([loaded, targetId]);
      }
      setAllForms((formsRes.data?.data || formsRes.data || []).filter(f => f.status === 'published' || true));
      setAllLobbies(Array.isArray(lobbiesRes.data) ? lobbiesRes.data : (lobbiesRes.data?.data || []));
      setPageLoaded(true);
    }).catch(() => setPageLoaded(true));
  }, [event._id]);

  const savePage = useCallback(async (pagesToSave, layout) => {
    if (!event._id) return;
    setSaveStatus('saving');
    try {
      const payload = (p, idx) => ({
        title: p.title, slug: p.slug, sections: p.sections,
        order: idx, published: p.published, isDefault: p.isDefault,
        headerOverride: p.headerOverride, footerOverride: p.footerOverride,
      });

      const [, ...pageResults] = await Promise.all([
        api.patch(`/events/${event._id}/layout`, { layout }),
        ...pagesToSave.map((p, idx) =>
          p._isNew
            ? api.post(`/events/${event._id}/pages`, payload(p, idx)).then(r => r.data)
            : api.patch(`/events/${event._id}/pages/${p._id}`, payload(p, idx)).then(r => r.data)
        ),
      ]);

      if (pageResults.some(r => !r.success)) throw new Error('One or more pages failed to save');

      setPagesAndActive(([prevPages, prevActive]) => {
        let nextActive = prevActive;
        const next = prevPages.map((p, idx) => {
          const res = pageResults[idx];
          if (p._isNew && res?.data?._id) {
            const realId = String(res.data._id);
            if (p._id === prevActive) nextActive = realId;
            return { ...p, _id: realId, _isNew: false };
          }
          return p;
        });
        return [next, nextActive];
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [event._id]);

  React.useEffect(() => {
    if (!pageLoaded || !event._id) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (slugUnavailable) return;
    saveTimerRef.current = setTimeout(() => savePage(pages, globalLayout), 20000);
    return () => clearTimeout(saveTimerRef.current);
  }, [pages, globalLayout, pageLoaded, event._id, slugUnavailable]);

  // ── Drag context ───────────────────────────────────────────────────────────
  const drag            = useRef(null);
  const canvasScrollRef = useRef(null);
  const activeDropId    = useRef(null);
  const sectionDrag     = useRef(null);
  const breakpointRef   = useRef('desktop');
  const _resets         = useRef(new Set());
  const resetAll = useCallback(() => { _resets.current.forEach(fn => fn()); activeDropId.current = null; sectionDrag.current = null; }, []);
  const dragCtx  = useRef({ drag, activeDropId, sectionDrag, breakpointRef, _resets: _resets.current, resetAll }).current;
  dragCtx.resetAll      = resetAll;
  breakpointRef.current = breakpoint;

  React.useEffect(() => {
    const handler = () => { drag.current = null; resetAll(); };
    window.addEventListener('dragend', handler);
    return () => window.removeEventListener('dragend', handler);
  }, [resetAll]);

  // ── Selected element lookup ────────────────────────────────────────────────
  const selectedEl = (() => {
    function findDeep(children) {
      for (const c of children) {
        if (c.id === selectedId) return c;
        if (c.type === 'container') { const found = findDeep(c.children || []); if (found) return found; }
      }
      return null;
    }
    if (selectedId) { for (const s of sections) { const found = findDeep(s.elements); if (found) return found; } }
    if (selectedSectionId) {
      const s = sections.find(s => s.id === selectedSectionId);
      if (s) return { ...s, type: s.type === 'header' ? 'header' : s.type === 'footer' ? 'footer' : 'section' };
    }
    return null;
  })();

  // ── Section CRUD ───────────────────────────────────────────────────────────
  const addSection = () => {
    const footerIdx  = sections.findIndex(s => s.type === 'footer');
    const insertAt   = footerIdx === -1 ? sections.length : footerIdx;
    const newSection = { id: uid(), label: 'New Section', elements: [], style: { padding: '60px 48px', background: '#fff', minHeight: '200px' } };
    pushHistory([...sections.slice(0, insertAt), newSection, ...sections.slice(insertAt)]);
  };

  const handleDelete = (sectionId, elId) => {
    if (!elId) {
      const target = sections.find(s => s.id === sectionId);
      if (target?.type === 'header' || target?.type === 'footer') return;
      pushHistory(sections.filter(s => s.id !== sectionId)); setSelectedId(null); return;
    }
    const [next] = removeEl(sections, elId); pushHistory(next); setSelectedId(null);
  };

  const handleSectionReorder = useCallback((fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const lastIdx = sections.length - 1;
    if (fromIdx === 0 || toIdx === 0 || fromIdx === lastIdx || toIdx === lastIdx) return;
    const next = [...sections]; const [moved] = next.splice(fromIdx, 1); next.splice(toIdx, 0, moved);
    pushHistory(next);
  }, [sections, pushHistory]);

  const deepCloneSection = (s) => {
    const clone = JSON.parse(JSON.stringify(s));
    const reId  = (node) => { node.id = uid(); if (node.children) node.children = node.children.map(c => reId({ ...c })); return node; };
    clone.id = uid(); clone.elements = clone.elements.map(el => reId(el)); return clone;
  };

  const handleSectionDuplicate = useCallback((sectionId) => {
    const idx = sections.findIndex(s => s.id === sectionId); if (idx === -1) return;
    const clone = deepCloneSection(sections[idx]);
    pushHistory([...sections.slice(0, idx + 1), clone, ...sections.slice(idx + 1)]);
  }, [sections, pushHistory]);

  const [sectionClipboard, setSectionClipboard] = useState(null);
  const handleSectionCopy  = useCallback((id) => { const s = sections.find(s => s.id === id); if (s) setSectionClipboard(JSON.parse(JSON.stringify(s))); }, [sections]);
  const handleSectionPaste = useCallback((afterId) => {
    if (!sectionClipboard) return;
    const clone = deepCloneSection(sectionClipboard);
    const idx = sections.findIndex(s => s.id === afterId); const at = idx === -1 ? sections.length : idx + 1;
    pushHistory([...sections.slice(0, at), clone, ...sections.slice(at)]);
  }, [sections, sectionClipboard, pushHistory]);

  // ── Element clipboard ──────────────────────────────────────────────────────
  const deepCloneEl = (el) => { const reId = (node) => { node.id = uid(); if (node.children) node.children = node.children.map(c => reId({ ...c })); return node; }; return reId(JSON.parse(JSON.stringify(el))); };

  const handleDuplicate = useCallback((elId) => {
    let found = false;
    const tryContainer = (children) => { const idx = children.findIndex(c => c.id === elId); if (idx !== -1) { found = true; return [...children.slice(0, idx + 1), deepCloneEl(children[idx]), ...children.slice(idx + 1)]; } return children.map(c => c.type === 'container' ? { ...c, children: tryContainer(c.children || []) } : c); };
    const next = sections.map(s => ({ ...s, elements: (() => { const idx = s.elements.findIndex(e => e.id === elId); if (idx !== -1) { found = true; const els = [...s.elements]; els.splice(idx + 1, 0, deepCloneEl(s.elements[idx])); return els; } return tryContainer(s.elements); })() }));
    if (found) pushHistory(next);
  }, [sections, pushHistory]);

  const handleCopy = useCallback((elId) => {
    let target = null;
    const search = (children) => { for (const c of children) { if (c.id === elId) { target = c; return; } if (c.type === 'container') search(c.children || []); } };
    sections.forEach(s => { const top = s.elements.find(e => e.id === elId); if (top) { target = top; return; } search(s.elements); });
    if (target) setClipboard(JSON.parse(JSON.stringify(target)));
  }, [sections]);

  const handlePaste = useCallback((afterElId) => {
    if (!clipboard) return;
    const reId = (node) => { node.id = uid(); if (node.children) node.children = node.children.map(c => reId({ ...c })); return node; };
    const clone = reId(JSON.parse(JSON.stringify(clipboard))); let found = false;
    const tryContainer = (children) => { const idx = children.findIndex(c => c.id === afterElId); if (idx !== -1) { found = true; return [...children.slice(0, idx + 1), clone, ...children.slice(idx + 1)]; } return children.map(c => c.type === 'container' ? { ...c, children: tryContainer(c.children || []) } : c); };
    const next = sections.map(s => ({ ...s, elements: (() => { const idx = s.elements.findIndex(e => e.id === afterElId); if (idx !== -1) { found = true; const els = [...s.elements]; els.splice(idx + 1, 0, clone); return els; } return tryContainer(s.elements); })() }));
    if (found) pushHistory(next);
  }, [sections, clipboard, pushHistory]);

  // ── Canvas mutations ───────────────────────────────────────────────────────
  const handleUpdateEl = (elId, updater) => pushHistory(updateEl(sections, elId, updater));
  const handleChange   = (sectionId, updated) => pushHistory(sections.map(s => s.id === sectionId ? { ...s, elements: s.elements.map(e => e.id === updated.id ? updated : e) } : s));

  const handleDrop = (e, sectionId, insertIdx) => {
    const d = drag.current; const type = (d?.kind === 'palette' ? d.elementType : null) || e.dataTransfer.getData('elementType');
    if (!type || !ELEMENT_TYPES[type]) return;
    const newEl = type === 'container' ? { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)) } : { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)), children: [{ id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES[type].defaultData)) }] };
    pushHistory(insertIntoSection(sections, sectionId, newEl, insertIdx ?? (sections.find(s => s.id === sectionId)?.elements.length ?? 0))); drag.current = null;
  };

  const handleReorder = (sectionId, fromIdx, toIdx) => {
    pushHistory(sections.map(s => { if (s.id !== sectionId) return s; const els = [...s.elements]; const [moved] = els.splice(fromIdx, 1); els.splice(toIdx, 0, moved); return { ...s, elements: els }; }));
  };

  const handleInsertNewChildAt = (containerId, type, insertIdx) => {
    pushHistory(insertIntoContainer(sections, containerId, { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES[type].defaultData)) }, insertIdx)); drag.current = null;
  };

  const handleMoveTopLevelIntoContainer = useCallback((elId, fromSectionId, toContainerId, insertIdx) => {
    if (isDescendantOrSelf(sections, elId, toContainerId)) return;
    const [without, el] = removeEl(sections, elId); if (!el) return;
    pushHistory(insertIntoContainer(without, toContainerId, el, insertIdx)); drag.current = null;
  }, [sections, pushHistory]);

  const handleMoveChildToSection = (childId, sectionId, insertIdx) => {
    const [without, el] = removeEl(sections, childId); if (!el) return;
    const wrapped = el.type === 'container' ? el : { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)), children: [el] };
    pushHistory(insertIntoSection(without, sectionId, wrapped, insertIdx)); drag.current = null;
  };

  const handleMoveChildToContainer = (childId, fromContainerId, toContainerId, insertIdx) => {
    const [without, el] = removeEl(sections, childId); if (!el) return;
    pushHistory(insertIntoContainer(without, toContainerId, el, insertIdx)); drag.current = null;
  };

  const handleMergeAsRow = useCallback((sectionId, targetId, draggedId, targetIdx, elementType) => {
    let base = sections, draggedEl = null;
    if (draggedId) { if (isDescendantOrSelf(sections, draggedId, targetId)) return; const [without, removed] = removeEl(base, draggedId); if (!removed) return; base = without; draggedEl = removed; }
    else if (elementType) { draggedEl = { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES[elementType].defaultData)) }; } else return;
    const targetSection = base.find(s => s.id === sectionId); if (!targetSection) return;
    const newTargetIdx  = targetSection.elements.findIndex(e => e.id === targetId); if (newTargetIdx === -1) return;
    const rowContainer  = { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)), orientation: 'row', style: { ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData.style)), padding: '16px', gap: '16px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', minHeight: '60px' }, children: [targetSection.elements[newTargetIdx], draggedEl] };
    pushHistory(base.map(s => { if (s.id !== sectionId) return s; const els = s.elements.filter(e => e.id !== targetId); els.splice(newTargetIdx, 0, rowContainer); return { ...s, elements: els }; })); drag.current = null;
  }, [sections, pushHistory]);

  const handleSplitDropIntoEmpty = useCallback((containerId, droppedElId, side, existingChildren = []) => {
    const colStyle = { padding: '12px', minHeight: '60px', background: 'transparent', borderRadius: '4px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'stretch', alignSelf: 'stretch' };
    const [without, droppedEl] = removeEl(sections, droppedElId); if (!droppedEl) return;
    pushHistory(updateEl(without, containerId, c => ({ ...c, orientation: 'row', style: { ...c.style, flexDirection: 'row', gap: '16px' }, children: [{ id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)), style: colStyle, children: side === 'left'  ? [droppedEl] : existingChildren }, { id: uid(), ...JSON.parse(JSON.stringify(ELEMENT_TYPES.container.defaultData)), style: colStyle, children: side === 'right' ? [droppedEl] : existingChildren }] })));
    drag.current = null;
  }, [sections, pushHistory]);

  const handleLayerMove = (d, toSectionId, toContainerId, toSlot) => {
    const { elId, fromContainerId, fromSectionId, fromIdx } = d;
    if (toContainerId) {
      if (isDescendantOrSelf(sections, elId, toContainerId)) return;
      if (fromContainerId === toContainerId) { const to = toSlot > fromIdx ? toSlot - 1 : toSlot; if (fromIdx !== to) pushHistory(reorderInContainer(sections, toContainerId, fromIdx, to)); }
      else if (fromContainerId) { handleMoveChildToContainer(elId, fromContainerId, toContainerId, toSlot); }
      else { handleMoveTopLevelIntoContainer(elId, fromSectionId, toContainerId, toSlot); }
    } else {
      if (!fromContainerId && fromSectionId === toSectionId) { const to = toSlot > fromIdx ? toSlot - 1 : toSlot; if (fromIdx !== to) handleReorder(toSectionId, fromIdx, to); }
      else if (!fromContainerId && fromSectionId !== toSectionId) { const [without, moved] = removeEl(sections, elId); if (!moved) return; pushHistory(insertIntoSection(without, toSectionId, moved, toSlot)); }
      else if (fromContainerId) { handleMoveChildToSection(elId, toSectionId, toSlot); }
    }
  };

  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && previewMode) setPreviewMode(false); };
    window.addEventListener('keydown', handler); return () => window.removeEventListener('keydown', handler);
  }, [previewMode]);

  const bpWidth      = BREAKPOINTS.find(b => b.key === breakpoint)?.width || '100%';
  const ctxMenuValue = useRef({ open: (elId, x, y) => setContextMenu({ elId, x, y }) }).current;

  const handleUpdateCurrentPage = useCallback((patch) => {
    setPages(prev => prev.map(p => {
      if (p._id === activePageId) return { ...p, ...patch };
      // If we're setting a new default, clear it from all other pages
      if (patch.isDefault === true) return { ...p, isDefault: false };
      return p;
    }));
  }, [activePageId, setPages]);

  // ── Apply template ─────────────────────────────────────────────────────────
  const handleApplyTemplate = useCallback((template) => {
    // Keep the current header and footer; swap out everything in between
    const currentSections = sections;
    const header = currentSections.find(s => s.type === 'header') || makeHeaderSection();
    const footer = currentSections.find(s => s.type === 'footer') || makeFooterSection();
    const newSections = [header, ...template.sections, footer];
    pushHistory(newSections);
  }, [sections, pushHistory]);

  // ── Template thumbnails (for wizard) ───────────────────────────────────────
  const wizardThumbs = useTemplateThumbnails(PAGE_TEMPLATES);

  // ── Visit Page helpers ─────────────────────────────────────────────────────
  const getVisitUrl = useCallback(() => {
    return `${process.env.REACT_APP_FRONTEND_URL || ''}/${event.slug}/${currentPage?.slug}`;
  }, [event.slug, currentPage?.slug]);

  const handleVisitPage = useCallback(async () => {
    if (!currentPage?.published) { setVisitModalOpen(true); return; }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    await savePage(pages, globalLayout);
    window.open(getVisitUrl(), '_blank', 'noopener,noreferrer');
  }, [currentPage?.published, pages, globalLayout, savePage, getVisitUrl]);

  const handlePublishAndVisit = useCallback(async () => {
    setVisitModalOpen(false);
    handleUpdateCurrentPage({ published: true });
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    // pages state hasn't re-rendered yet with published:true, so patch it inline for save
    const updatedPages = pages.map(p =>
      p._id === currentPage?._id ? { ...p, published: true } : p
    );
    await savePage(updatedPages, globalLayout);
    window.open(getVisitUrl(), '_blank', 'noopener,noreferrer');
  }, [pages, currentPage?._id, globalLayout, savePage, handleUpdateCurrentPage, getVisitUrl]);

  // Sticky height for content sections
  const isPill             = (activeHeaderConfig.template || 'classic') === 'pill';
  const stickyHeaderHeight = isPill ? (activeHeaderConfig.style?.pillTop ?? 16) + 56 + 8 : 0;

  return (
    <PreviewCtx.Provider value={previewMode}>
      <CtxMenuCtx.Provider value={ctxMenuValue}>
        <DragCtx.Provider value={dragCtx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', background: '#f0f4f8' }}
            onClick={() => { setSelectedId(null); setSelectedSectionId(null); }}>

            {/* ── Toolbar ── */}
            <Box onClick={e => e.stopPropagation()} sx={{ background: '#fff', borderBottom: '1px solid #e8edf2', px: 3, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Tooltip title="Undo"><IconButton size="small" onClick={undo} disabled={histIdx === 0}><UndoOutlined sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                <Tooltip title="Redo"><IconButton size="small" onClick={redo} disabled={histIdx === history.length - 1}><RedoOutlined sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                {event._id && (
                  <Tooltip title={slugUnavailable ? 'Fix the unavailable slug before saving' : saveStatus === 'saved' ? 'Saved!' : saveStatus === 'error' ? 'Save failed — click to retry' : 'Save page'}>
                    <Box onClick={() => { if (slugUnavailable || saveStatus === 'saving') return; if (saveTimerRef.current) clearTimeout(saveTimerRef.current); savePage(pages, globalLayout); }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, px: 1.25, height: 30, borderRadius: 1.5, fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s', ...(slugUnavailable ? { background: alpha('#ef4444', 0.08), color: '#dc2626', border: '1px solid #fca5a5', cursor: 'not-allowed', opacity: 0.7 } : saveStatus === 'saved' ? { background: alpha('#22c55e', 0.1), color: '#16a34a', border: '1px solid #86efac', cursor: 'pointer' } : saveStatus === 'error' ? { background: alpha('#ef4444', 0.1), color: '#dc2626', border: '1px solid #fca5a5', cursor: 'pointer' } : saveStatus === 'saving' ? { background: '#f0f4f8', color: '#9ea8b3', border: '1px solid #e4e9f0', cursor: 'default' } : { background: '#f0f4f8', color: '#5a6474', border: '1px solid #e4e9f0', cursor: 'pointer', '&:hover': { background: alpha(PRIMARY, 0.06), color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } }) }}>
                      {saveStatus === 'saved' && !slugUnavailable && <CheckCircleOutlined sx={{ fontSize: 14 }} />}
                      {(saveStatus === 'error' || slugUnavailable) && <ErrorOutlined sx={{ fontSize: 14 }} />}
                      {saveStatus !== 'saved' && saveStatus !== 'error' && !slugUnavailable && <SaveOutlined sx={{ fontSize: 14 }} />}
                      <Box component="span">{slugUnavailable ? 'Slug conflict' : saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved' : saveStatus === 'error' ? 'Error' : 'Save'}</Box>
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: 'flex', background: '#f0f4f8', border: '1px solid #e4e9f0', borderRadius: 2, p: '2px', gap: '2px' }}>
                {BREAKPOINTS.map(bp => {
                  const hasOverrides = bp.key !== 'desktop' && selectedEl && selectedEl.type !== 'section' && Object.keys(selectedEl.responsiveStyles?.[bp.key] || {}).length > 0;
                  return (
                    <Tooltip key={bp.key} title={bp.label}>
                      <Box sx={{ position: 'relative', display: 'flex' }}>
                        <IconButton size="small" onClick={() => setBreakpoint(bp.key)} sx={{ width: 32, height: 30, borderRadius: 1.5, color: breakpoint === bp.key ? PRIMARY : '#9ea8b3', background: breakpoint === bp.key ? '#fff' : 'transparent', boxShadow: breakpoint === bp.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                          {bp.icon}
                        </IconButton>
                        {hasOverrides && <Box sx={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', background: bp.key === 'tablet' ? '#f59e0b' : '#ef4444', border: '1px solid #fff', pointerEvents: 'none' }} />}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>

              {/* ── Right action group: Gear + Preview + Visit Page ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                {/* Gear / Page Settings */}
                <Tooltip title="Page settings">
                  <Box onClick={e => setSettingsAnchor(e.currentTarget)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 2, cursor: 'pointer', color: settingsAnchor ? PRIMARY : '#5a6474', border: `1px solid ${settingsAnchor ? alpha(PRIMARY, 0.4) : '#e8edf2'}`, background: settingsAnchor ? alpha(PRIMARY, 0.06) : 'transparent', transition: 'all 0.15s', '&:hover': { color: PRIMARY, borderColor: alpha(PRIMARY, 0.3) } }}>
                    <SettingsOutlined sx={{ fontSize: 16 }} />
                  </Box>
                </Tooltip>

                {/* Grouped Preview + Visit Page */}
                <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e8edf2', borderRadius: 2, overflow: 'hidden' }}>
                  <Box onClick={() => { setPreviewMode(v => !v); setSelectedId(null); }}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', px: 1.5, py: 0.75, transition: 'all 0.15s', ...(previewMode ? { color: '#fff', background: PRIMARY } : { color: '#5a6474', '&:hover': { color: PRIMARY, background: alpha(PRIMARY, 0.04) } }) }}>
                    {previewMode ? <VisibilityOffOutlined sx={{ fontSize: 16 }} /> : <VisibilityOutlined sx={{ fontSize: 16 }} />}
                    {previewMode ? 'Exit Preview' : 'Preview'}
                  </Box>
                  {event?.slug && currentPage?.slug && (
                    <>
                      <Box sx={{ width: '1px', alignSelf: 'stretch', background: '#e8edf2' }} />
                      <Tooltip title="Save and open live page in a new tab">
                        <Box
                          onClick={handleVisitPage}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', px: 1.5, py: 0.75, transition: 'all 0.15s', color: '#5a6474', '&:hover': { color: PRIMARY, background: alpha(PRIMARY, 0.04) } }}
                        >
                          <OpenInNewOutlined sx={{ fontSize: 16 }} />
                          Visit Page
                        </Box>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>

              {/* Page Settings Popover */}
              <Popover
                open={Boolean(settingsAnchor)}
                anchorEl={settingsAnchor}
                onClose={() => setSettingsAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: 1, borderRadius: 2, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e8edf2', width: 280, overflow: 'hidden' } } }}
              >
                <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #f0f2f5', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsOutlined sx={{ fontSize: 14, color: PRIMARY }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1a1a2e' }}>Page Settings</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#9ea8b3', ml: 'auto', fontStyle: 'italic', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentPage?.title}</Typography>
                </Box>
                <PageSettingsContent
                  page={currentPage}
                  onUpdatePage={handleUpdateCurrentPage}
                  event={event}
                  onSlugAvailabilityChange={avail => setSlugUnavailable(avail === false)}
                />
              </Popover>

              {/* ── Unpublished Page Modal ── */}
              <Dialog
                open={visitModalOpen}
                onClose={() => setVisitModalOpen(false)}
                maxWidth="xs"
                fullWidth
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)', background: 'rgba(10,15,30,0.55)' } } }}
                PaperProps={{ sx: { borderRadius: 3, p: 0, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' } }}
              >
                <DialogContent sx={{ p: 0 }}>
                  {/* Icon band */}
                  <Box sx={{ background: alpha('#f59e0b', 0.08), borderBottom: '1px solid', borderColor: alpha('#f59e0b', 0.15), px: 3, pt: 3, pb: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: alpha('#f59e0b', 0.12), border: `1.5px solid ${alpha('#f59e0b', 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <PublicOutlined sx={{ fontSize: 24, color: '#d97706' }} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>Page not published</Typography>
                      <Typography sx={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.5 }}>
                        This page is currently private. You need to publish it before visitors can see it.
                      </Typography>
                    </Box>
                  </Box>
                  {/* Actions */}
                  <Box sx={{ px: 3, py: 2.5, display: 'flex', gap: 1.5 }}>
                    <Box onClick={() => setVisitModalOpen(false)}
                      sx={{ flex: 1, py: 1.1, borderRadius: 2, border: '1px solid #e4e9f0', background: '#f8f9fb', color: '#5a6474', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', '&:hover': { background: '#f0f2f5', borderColor: '#d1d9e0' } }}>
                      Cancel
                    </Box>
                    <Box onClick={handlePublishAndVisit}
                      sx={{ flex: 1.4, py: 1.1, borderRadius: 2, background: PRIMARY, color: '#fff', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, '&:hover': { background: alpha(PRIMARY, 0.88) } }}>
                      <PublicOutlined sx={{ fontSize: 15 }} />
                      Publish &amp; Visit Page
                    </Box>
                  </Box>
                </DialogContent>
              </Dialog>

            </Box>

            {/* ── Body ── */}
            <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

              {/* Left panel */}
              {!previewMode && (
                <Box onClick={e => e.stopPropagation()} sx={{ width: 230, flexShrink: 0, borderRight: '1px solid #e8edf2', background: '#fafbfc', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  {selectedEl?.type === 'header'
                    ? <HeaderInspector
                        section={{ ...selectedEl, headerConfig: activeHeaderConfig }}
                        isOverride={currentPage.headerOverride}
                        onEnableOverride={enableHeaderOverride}
                        onDisableOverride={disableHeaderOverride}
                        onUpdate={handleUpdateHeaderSection}
                        onClose={() => setSelectedSectionId(null)}
                        allPages={pages}
                        allSections={contentSections}
                        allForms={allForms}
                        allLobbies={allLobbies}
                        event={event}
                      />
                    : selectedEl?.type === 'footer'
                      ? <FooterInspector
                          section={{ ...selectedEl, footerConfig: activeFooterConfig }}
                          isOverride={currentPage.footerOverride}
                          onEnableOverride={enableFooterOverride}
                          onDisableOverride={disableFooterOverride}
                          onUpdate={handleUpdateFooterSection}
                          onClose={() => setSelectedSectionId(null)}
                          allPages={pages}
                          allSections={contentSections}
                        />
                      : selectedEl?.type === 'section'
                        ? <SectionSettings section={selectedEl} onUpdate={handleUpdateSection} onClose={() => setSelectedSectionId(null)} />
                        : selectedEl?.type === 'container'
                          ? <ContainerSettings el={selectedEl} onUpdate={handleUpdateSelectedEl} onClose={() => setSelectedId(null)} breakpoint={breakpoint} />
                          : selectedEl?.type === 'text'
                            ? <TextInspector el={selectedEl} onUpdate={handleUpdateSelectedEl} onClose={() => setSelectedId(null)} breakpoint={breakpoint} />
                            : selectedEl?.type === 'button'
                              ? <ButtonInspector el={selectedEl} onUpdate={handleUpdateSelectedEl} onClose={() => setSelectedId(null)} breakpoint={breakpoint} />
                              : selectedEl?.type === 'image'
                                ? <ImageInspector el={selectedEl} onUpdate={handleUpdateSelectedEl} onClose={() => setSelectedId(null)} breakpoint={breakpoint} />
                                : selectedEl?.type === 'countdown'
                                  ? <CountdownInspector el={selectedEl} onUpdate={handleUpdateSelectedEl} onClose={() => setSelectedId(null)} />
                                  : selectedEl?.type === 'speakers'
                                    ? <SpeakersInspector el={selectedEl} onUpdate={handleUpdateSelectedEl} onClose={() => setSelectedId(null)} breakpoint={breakpoint} />
                                    : selectedEl?.type === 'schedule'
                                      ? <ScheduleInspector el={selectedEl} onUpdate={handleUpdateSelectedEl} onClose={() => setSelectedId(null)} breakpoint={breakpoint} />
                                      : <LeftPanelIdle
                                    page={currentPage}
                                    onUpdatePage={handleUpdateCurrentPage}
                                    onDragStart={(e, type) => { drag.current = { kind: 'palette', elementType: type }; e.dataTransfer.setData('elementType', type); }}
                                    onApplyTemplate={handleApplyTemplate}
                                  />
                  }
                </Box>
              )}

              {/* Canvas column */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                {!previewMode && (
                  <PageTabs pages={pages} activePageId={activePageId} onSwitch={handleSwitchPage} onAdd={handleAddPage} onRename={handleRenamePage} onDuplicate={handleDuplicatePage} onDelete={handleDeletePage} />
                )}
                <Box ref={canvasScrollRef} sx={{ flex: 1, overflowY: 'auto', p: contentSections.length === 0 && !previewMode ? 0 : 3, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflow: 'auto' }}>
                  {/* ── Page Wizard ── show when page has no content sections */}
                  {contentSections.length === 0 && !previewMode ? (
                    <PageWizard
                      thumbnails={wizardThumbs}
                      onApplyTemplate={(tpl) => handleApplyTemplate(tpl)}
                      onBlank={() => {
                        // Add a single blank section so the wizard dismisses
                        const insertAt = sections.findIndex(s => s.type === 'footer');
                        const blankSection = { id: uid(), label: 'Section', type: 'section', elements: [], style: { padding: '60px 48px', background: '#fff', minHeight: '200px' } };
                        const next = [...sections];
                        next.splice(insertAt === -1 ? next.length : insertAt, 0, blankSection);
                        pushHistory(next);
                      }}
                    />
                  ) : (
                  <Box sx={{ width: bpWidth, maxWidth: '100%', transition: 'width 0.3s ease', background: '#fff', overflow: 'visible', boxShadow: '0 2px 20px rgba(0,0,0,0.08)', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>

                    {headerSection && (
                      <HeaderSection
                        section={headerSection}
                        config={activeHeaderConfig}
                        selected={selectedSectionId === headerSection.id}
                        onSelectHeader={selectSection}
                        onUpdateConfig={(patch) => handleUpdateHeaderSection({ ...headerSection, headerConfig: { ...activeHeaderConfig, ...patch } })}
                        selectedId={selectedId}
                        allPages={pages}
                        allSections={contentSections}
                        allForms={allForms}
                        allLobbies={allLobbies}
                        eventSlug={event?.slug || ''}
                        eventUrlMode={event?.urlMode || 'prefixed'}
                        currentPageSlug={currentPage?.slug || ''}
                        breakpoint={breakpoint}
                        canvasScrollRef={canvasScrollRef}
                        loginRedirectHref={event?.login?.loginRedirectHref ?? null}
                        onLoginRedirectChange={handleLoginRedirectChange}
                      />
                    )}

                    <PageCtx.Provider value={{ allPages: pages, allForms, allLobbies, allSections: [], eventSlug: event?.slug || '', eventUrlMode: event?.urlMode || 'prefixed', currentPageSlug: currentPage?.slug || '', loginRedirectHref: event?.login?.loginRedirectHref ?? null, onLoginRedirectChange: handleLoginRedirectChange, speakers: localSpeakers, onSpeakersUpdate: handleSpeakersUpdate, schedule: localSchedule, onScheduleUpdate: handleScheduleUpdate, eventId: event?._id || null, eventBannerUrl: event?.branding?.bannerUrl || event?.coverImage || null }}>
                    <Box sx={{ flex: 1 }}>
                      {contentSections.map((section, idx) => (
                        <Box key={section.id} sx={{ borderBottom: !previewMode && idx < contentSections.length - 1 ? '1px dashed #e8edf2' : 'none' }}>
                          <Section
                            section={section} sectionIdx={idx} sectionsCount={contentSections.length}
                            stickyHeaderHeight={idx === 0 ? stickyHeaderHeight : 0}
                            selectedId={selectedId} selectedSectionId={selectedSectionId}
                            onSelectSection={selectSection} onSelect={selectEl}
                            onChange={handleChange} onDelete={handleDelete} onDrop={handleDrop}
                            onSelectChild={selectEl} onReorder={handleReorder}
                            onSectionReorder={handleSectionReorder}
                            onSectionDuplicate={handleSectionDuplicate}
                            onSectionCopy={handleSectionCopy}
                            onSectionPaste={handleSectionPaste}
                            sectionClipboard={sectionClipboard}
                            onUpdateEl={handleUpdateEl}
                            onDeleteEl={(elId) => handleDelete(section.id, elId)}
                            onMoveChildToSection={handleMoveChildToSection}
                            onMoveChildToContainer={handleMoveChildToContainer}
                            onInsertNewChildAt={handleInsertNewChildAt}
                            onMoveTopLevelIntoContainer={handleMoveTopLevelIntoContainer}
                            onRename={handleRenameSection}
                            onMergeAsRow={handleMergeAsRow}
                            onSplitDropIntoEmpty={handleSplitDropIntoEmpty}
                          />
                        </Box>
                      ))}
                      {!previewMode && (
                        <Box onClick={addSection} sx={{ py: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: alpha(PRIMARY, 0.5), cursor: 'pointer', borderTop: contentSections.length > 0 ? '1px dashed #e8edf2' : 'none', transition: 'all 0.15s', '&:hover': { background: alpha(PRIMARY, 0.03), color: PRIMARY } }}>
                          <AddOutlined sx={{ fontSize: 20 }} />
                          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'inherit' }}>Add Section</Typography>
                        </Box>
                      )}
                    </Box>
                    </PageCtx.Provider>

                    {footerSection && (
                      <FooterSection
                        section={footerSection}
                        config={activeFooterConfig}
                        selected={selectedSectionId === footerSection.id}
                        onSelectFooter={selectSection}
                        breakpoint={breakpoint}
                      />
                    )}

                  </Box>
                  )}
                </Box>
              </Box>

              {/* Layers panel */}
              {!previewMode && (
                <LayersPanel
                  sections={sections} selectedId={selectedId} selectedSectionId={selectedSectionId}
                  onSelect={selectEl} onSelectSection={selectSection} onDelete={handleDelete}
                  onLayerMove={handleLayerMove} onSectionReorder={handleSectionReorder}
                  onSectionDuplicate={handleSectionDuplicate} onSectionCopy={handleSectionCopy}
                  onSectionPaste={handleSectionPaste} sectionClipboard={sectionClipboard}
                  onRenameSection={handleRenameSection} onRenameEl={handleRenameEl}
                  headerOverride={currentPage.headerOverride}
                  footerOverride={currentPage.footerOverride}
                />
              )}
            </Box>
          </Box>

          <ContextMenu
            menu={contextMenu}
            onDuplicate={handleDuplicate} onCopy={handleCopy} onPaste={handlePaste} clipboard={clipboard}
            onSectionDuplicate={handleSectionDuplicate} onSectionCopy={handleSectionCopy}
            onSectionPaste={handleSectionPaste} onSectionDelete={(id) => handleDelete(id)}
            sectionClipboard={sectionClipboard}
            onDelete={(elId) => handleDelete(null, elId)}
            onClose={() => setContextMenu(null)}
          />
        </DragCtx.Provider>
      </CtxMenuCtx.Provider>
    </PreviewCtx.Provider>
  );
}